import { useEffect, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MiniKnowledgeCore from "@/components/MiniKnowledgeCore";
import CoinPurchaseModal from "@/components/CoinPurchaseModal";
import ChatWindow from "@/components/ChatWindow";
import PaymentHistory from "@/components/PaymentHistory";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { absoluteFileUrl } from "@/lib/utils_ext";
import { BookOpen, Video, Users, MessageCircle, Wallet, Zap, Trophy, Bookmark, Calendar, ArrowRight, FileText, Play, Star, User as UserIcon, Award, Brain, Sparkles } from "lucide-react";

export default function StudentDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [buyOpen, setBuyOpen] = useState(false);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (user && user.role !== "student") navigate("/teacher/dashboard");
  }, [user, loading, navigate]);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 pt-28 pb-16 relative">
        <div className="absolute top-4 right-0 hidden lg:block pointer-events-none opacity-60">
          <MiniKnowledgeCore size={180} accent="indigo" />
        </div>
        <div className="mb-8 relative">
          <div className="text-xs uppercase tracking-widest font-semibold text-primary">Class {user.student_class} · {user.board || "CBSE"} · {user.city}</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">
            Hey {user.name?.split(" ")[0]} 👋
          </h1>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="glass rounded-full p-1 h-auto flex-wrap">
            {[
              { v: "overview", i: Zap, l: "Overview" },
              { v: "certificates", i: Award, l: "Certificates" },
              { v: "bookmarks", i: Bookmark, l: "Bookmarks" },
              { v: "demos", i: Calendar, l: "Demos" },
              { v: "chats", i: MessageCircle, l: "Chats" },
              { v: "coins", i: Wallet, l: "Coins" },
            ].map(t => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}
                className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <t.i className="w-4 h-4 mr-1.5" /> {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="mt-8"><Overview onBuy={() => setBuyOpen(true)} /></TabsContent>
          <TabsContent value="certificates" className="mt-8"><Certificates /></TabsContent>
          <TabsContent value="bookmarks" className="mt-8"><Bookmarks /></TabsContent>
          <TabsContent value="demos" className="mt-8"><Demos /></TabsContent>
          <TabsContent value="chats" className="mt-8"><Chats /></TabsContent>
          <TabsContent value="coins" className="mt-8"><PaymentHistory onBuy={() => setBuyOpen(true)} /></TabsContent>
        </Tabs>
      </div>
      <CoinPurchaseModal open={buyOpen} onClose={() => setBuyOpen(false)} />
      <Footer />
    </div>
  );
}

function Overview({ onBuy }) {
  const { user } = useAuth();
  const [streak, setStreak] = useState({ streak: 0, total_active_days: 0 });
  const [progress, setProgress] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [recommended, setRecommended] = useState({ notes: [], videos: [] });
  const [xp, setXp] = useState({ xp: 0, level: 1, xp_to_next: 200 });
  const [daily, setDaily] = useState(null);

  useEffect(() => {
    api.get("/me/streak").then(r => setStreak(r.data)).catch(() => {});
    api.get("/progress/videos").then(r => setProgress(r.data.slice(0, 3))).catch(() => {});
    api.get("/bookmarks").then(r => setBookmarks(r.data.slice(0, 3))).catch(() => {});
    api.get("/me/weekly-activity").then(r => setWeekly(r.data)).catch((e) => console.error("weekly", e));
    api.get("/me/recommended").then(r => setRecommended(r.data)).catch((e) => console.error("rec", e));
    api.get("/me/xp").then(r => setXp(r.data)).catch(() => {});
    api.get("/daily-challenge").then(r => setDaily(r.data)).catch(() => {});
  }, []);

  const achievements = [
    { key: "first_step", label: "First Step", unlocked: streak.total_active_days > 0, icon: "🌱" },
    { key: "week", label: "Week Warrior", unlocked: streak.streak >= 7, icon: "🔥" },
    { key: "month", label: "Monthly Master", unlocked: streak.streak >= 30, icon: "👑" },
    { key: "curious", label: "Curious Mind", unlocked: bookmarks.length >= 5, icon: "🔖" },
  ];

  return (
    <div className="space-y-8">
      {/* Streak + XP + coin cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="glass rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-orange-500/20 blur-2xl" />
          <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center mb-3">
            <Zap className="w-5 h-5 text-orange-500" />
          </div>
          <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Streak</div>
          <div className="font-display text-3xl font-black mt-1" data-testid="streak-value">{streak.streak}</div>
          <div className="text-xs text-muted-foreground">days</div>
        </div>
        <div className="glass rounded-2xl p-5 relative overflow-hidden" data-testid="xp-card">
          <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-indigo-500/20 blur-2xl" />
          <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-3">
            <Sparkles className="w-5 h-5 text-indigo-500" />
          </div>
          <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">XP</div>
          <div className="font-display text-3xl font-black mt-1">{xp.xp}</div>
          <div className="text-xs text-muted-foreground">Lvl {xp.level} · {xp.xp_to_next} to next</div>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
            <Wallet className="w-5 h-5 text-primary" />
          </div>
          <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Coins</div>
          <div className="font-display text-3xl font-black mt-1">{user.coins}</div>
          <button onClick={onBuy} className="text-xs text-primary font-semibold hover:underline mt-1">Buy more →</button>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center mb-3">
            <Play className="w-5 h-5 text-secondary" />
          </div>
          <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Videos</div>
          <div className="font-display text-3xl font-black mt-1">{progress.length ? progress.reduce((s, p) => s + 1, 0) : 0}</div>
          <div className="text-xs text-muted-foreground">watched</div>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center mb-3">
            <Trophy className="w-5 h-5 text-accent" />
          </div>
          <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Badges</div>
          <div className="font-display text-3xl font-black mt-1">{achievements.filter(a => a.unlocked).length}/{achievements.length}</div>
        </div>
      </div>

      {/* Daily challenge */}
      {daily?.quiz && (
        <div className="glass rounded-3xl p-6 relative overflow-hidden" data-testid="dashboard-daily-challenge">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-primary/20 blur-3xl" />
          <div className="relative flex items-center gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-lg shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-primary">Daily challenge</div>
              <div className="font-display text-xl font-bold truncate">{daily.quiz.title}</div>
              <div className="text-xs text-muted-foreground">
                Class {daily.quiz.class_level} · {daily.quiz.subject} · {daily.quiz.question_count} Qs · +{daily.quiz.xp_reward} XP
              </div>
            </div>
            <Link to="/learn/quizzes" data-testid="dashboard-daily-play">
              <Button className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90">
                {daily.completed_today ? "Play again" : "Take it"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        {[
          { to: "/browse/notes", icon: FileText, title: "Study notes", desc: "Chapter-organized", color: "from-cyan-500 to-blue-500" },
          { to: "/browse/learnings", icon: Video, title: "Watch lessons", desc: "With progress tracking", color: "from-indigo-500 to-purple-500" },
          { to: "/learn/quizzes", icon: Brain, title: "Take a quiz", desc: "Earn XP + certificates", color: "from-purple-500 to-pink-500" },
          { to: "/learn/flashcards", icon: Sparkles, title: "Flashcards", desc: "Recall & reinforce", color: "from-emerald-500 to-cyan-500" },
        ].map((a, i) => (
          <Link key={i} to={a.to} data-testid={`quick-${a.to.replace(/\//g, "-")}`}
            className="glass rounded-3xl p-6 card-3d flex items-center gap-4 group">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${a.color} flex items-center justify-center shadow-lg`}>
              <a.icon className="w-6 h-6 text-white" strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-lg">{a.title}</div>
              <div className="text-sm text-muted-foreground">{a.desc}</div>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 group-hover:text-foreground transition-all" />
          </Link>
        ))}
      </div>

      {/* Achievements */}
      <div>
        <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-4">Achievements</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {achievements.map(a => (
            <div key={a.key} className={`glass rounded-2xl p-5 text-center ${!a.unlocked && "opacity-40 grayscale"}`}
              data-testid={`achievement-${a.key}`}>
              <div className="text-4xl mb-2">{a.icon}</div>
              <div className="font-semibold text-sm">{a.label}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                {a.unlocked ? "Unlocked" : "Locked"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly activity */}
      {weekly.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-4">This week</div>
          <div className="glass rounded-2xl p-6" data-testid="weekly-activity">
            <div className="flex items-end justify-between gap-2 h-24">
              {weekly.map((d) => (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-2" data-testid={`weekly-${d.label}`}>
                  <div className="w-full flex-1 flex items-end">
                    <div
                      className={`w-full rounded-lg transition-all ${d.active ? "bg-gradient-to-t from-primary to-secondary" : "bg-muted"}`}
                      style={{ height: d.active ? "100%" : "22%" }}
                    />
                  </div>
                  <div className={`text-xs font-medium ${d.active ? "text-foreground" : "text-muted-foreground"}`}>
                    {d.label}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-xs text-muted-foreground text-center">
              {weekly.filter(d => d.active).length} of 7 days active this week
            </div>
          </div>
        </div>
      )}

      {/* Recommended */}
      {(recommended.notes.length > 0 || recommended.videos.length > 0) && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Recommended for you</div>
            <Link to="/browse/notes" className="text-xs text-primary font-semibold">Browse all →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="recommended-grid">
            {recommended.notes.slice(0, 3).map(n => (
              <Link key={n.note_id} to={`/browse/notes?class=${n.class_level}&subject=${encodeURIComponent(n.subject)}`}
                data-testid={`rec-note-${n.note_id}`}
                className="glass rounded-2xl p-5 card-3d">
                <div className="flex items-center gap-2 text-xs mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">Note</span>
                  <span className="text-muted-foreground">Class {n.class_level} · {n.subject}</span>
                </div>
                <div className="font-semibold text-sm line-clamp-2">{n.title}</div>
                <div className="text-xs text-muted-foreground mt-1">{n.chapter}</div>
              </Link>
            ))}
            {recommended.videos.slice(0, 3).map(v => (
              <Link key={v.video_id} to={`/browse/learnings?class=${v.class_level}&subject=${encodeURIComponent(v.subject)}`}
                data-testid={`rec-video-${v.video_id}`}
                className="glass rounded-2xl p-5 card-3d">
                <div className="flex items-center gap-2 text-xs mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-secondary/10 text-secondary font-medium">Video</span>
                  <span className="text-muted-foreground">Class {v.class_level} · {v.subject}</span>
                </div>
                <div className="font-semibold text-sm line-clamp-2">{v.title}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Resume watching */}
      {progress.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Continue watching</div>
            <Link to="/browse/learnings" className="text-xs text-primary font-semibold">See all →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {progress.map(p => (
              <Link key={p.video_id} to="/browse/learnings" className="glass rounded-2xl p-4 card-3d flex gap-3">
                {p.video?.thumbnail && <img src={p.video.thumbnail} alt="" className="w-20 h-14 rounded-lg object-cover shrink-0" />}
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground truncate">Class {p.video?.class_level} · {p.video?.subject}</div>
                  <div className="text-sm font-semibold truncate">{p.video?.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Bookmarks() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/bookmarks").then(r => setItems(r.data)); }, []);
  if (items.length === 0) return (
    <div className="glass rounded-3xl p-16 text-center">
      <Bookmark className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
      <div className="font-display text-xl font-bold">Nothing saved yet</div>
      <div className="text-sm text-muted-foreground mt-1">Bookmark notes, videos, and tutors as you explore.</div>
    </div>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="bookmarks-grid">
      {items.map(b => (
        <div key={b.bookmark_id} className="glass rounded-2xl p-5 card-3d">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">{b.resource_type}</div>
          <div className="font-semibold">{b.item?.title || b.item?.name || "Saved item"}</div>
          {b.item?.description && <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{b.item.description}</div>}
        </div>
      ))}
    </div>
  );
}

function Demos() {
  const [demos, setDemos] = useState([]);
  useEffect(() => { api.get("/demos/mine").then(r => setDemos(r.data)); }, []);
  if (demos.length === 0) return (
    <div className="glass rounded-3xl p-16 text-center">
      <Calendar className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
      <div className="font-display text-xl font-bold">No demo bookings yet</div>
      <Link to="/browse/tutors" className="text-sm text-primary font-semibold mt-2 inline-block">Find a tutor →</Link>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="demos-list">
      {demos.map(d => (
        <div key={d.demo_id} className="glass rounded-2xl p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold">{d.teacher_name} · {d.subject}</div>
            <div className="text-xs text-muted-foreground">Preferred: {d.preferred_time}</div>
          </div>
          <div className={`text-xs px-3 py-1 rounded-full font-semibold uppercase tracking-wider ${
            d.status === "accepted" ? "bg-green-500/10 text-green-600" :
            d.status === "declined" ? "bg-red-500/10 text-red-600" :
            d.status === "completed" ? "bg-blue-500/10 text-blue-600" :
            "bg-yellow-500/10 text-yellow-600"
          }`}>{d.status}</div>
        </div>
      ))}
    </div>
  );
}

function Chats() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/messages/threads").then(r => setThreads(r.data)).catch(() => {}); }, []);
  return (
    <div>
      {threads.length === 0 ? (
        <div className="glass rounded-3xl p-16 text-center">
          <MessageCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
          <div className="font-display text-xl font-bold">No conversations yet</div>
          <div className="text-sm text-muted-foreground mt-1">Reveal a tutor's contact to start chatting.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {threads.map(t => (
            <button key={t.user.user_id} onClick={() => setActive(t.user)} data-testid={`thread-${t.user.user_id}`}
              className="w-full flex items-center gap-4 glass rounded-2xl p-4 text-left hover:bg-muted/30">
              {t.user.picture ? (
                <img src={t.user.picture} className="w-11 h-11 rounded-full object-cover" alt="" />
              ) : (
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center font-bold">{t.user.name?.[0]}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium">{t.user.name}</div>
                <div className="text-sm text-muted-foreground truncate">{t.last_message?.text}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      <ChatWindow open={!!active} onClose={() => setActive(null)} partner={active} />
    </div>
  );
}


function Certificates() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/certificates")
      .then(r => setCerts(r.data))
      .catch(() => setCerts([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-muted-foreground text-sm">Loading…</div>;

  if (certs.length === 0) {
    return (
      <div className="glass rounded-3xl p-16 text-center">
        <Award className="w-12 h-12 text-primary mx-auto mb-4 opacity-60" />
        <div className="font-display text-2xl font-bold">No certificates yet</div>
        <p className="text-muted-foreground mt-2 max-w-md mx-auto">
          Score 70% or higher on any quiz to earn a Luminon certificate.
        </p>
        <Link to="/learn/quizzes">
          <Button className="mt-6 rounded-full h-11 px-6 bg-primary hover:bg-primary/90" data-testid="cert-empty-cta">
            Take your first quiz <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="certificates-grid">
      {certs.map((c) => (
        <div
          key={c.cert_id}
          data-testid={`certificate-${c.cert_id}`}
          className="relative rounded-3xl p-7 overflow-hidden border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-cyan-500/10 to-indigo-500/10"
        >
          <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg">
                <Award className="w-5 h-5 text-white" />
              </div>
              <div className="text-3xl font-black font-display">{c.percent}%</div>
            </div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-600 dark:text-emerald-400">
              Luminon certificate
            </div>
            <div className="font-display text-xl font-bold mt-1 leading-tight">{c.quiz_title}</div>
            <div className="text-xs text-muted-foreground mt-2">
              Class {c.class_level} · {c.subject}
              {c.chapter ? ` · ${c.chapter}` : ""}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Issued {new Date(c.issued_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
